import { useSearchParams, Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { FadeUp } from "@/components/Reveal";

const TABS = [
  { id: "cgv", label: "CGV" },
  { id: "mentions-legales", label: "Mentions Légales" },
  { id: "confidentialite", label: "Confidentialité" },
  { id: "livraison-retours", label: "Livraison & Retours" },
];

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h3 className="font-serif text-xl text-onyx mb-3">{title}</h3>
    <div className="text-sm text-muted-warm leading-relaxed space-y-2">{children}</div>
  </div>
);

const CONTENT = {
  cgv: {
    title: "Conditions Générales de Vente",
    updated: "Dernière mise à jour : juillet 2026",
    body: (
      <>
        <Section title="1. Objet">
          <p>Les présentes Conditions Générales de Vente (CGV) régissent les ventes de produits cosmétiques proposées par Relax Beauté (ci-après « Relax ») sur le site internet, notamment le Masque Gel Hydratant au Bio-Collagène. Toute commande implique l'acceptation pleine et entière des présentes CGV.</p>
        </Section>
        <Section title="2. Produits et prix">
          <p>Les produits sont décrits avec la plus grande exactitude possible. Les prix sont indiqués en euros, toutes taxes comprises (TTC), hors frais de livraison. Relax se réserve le droit de modifier ses prix à tout moment, le prix applicable étant celui en vigueur au moment de la commande.</p>
        </Section>
        <Section title="3. Commande">
          <p>La commande est validée après vérification du panier, saisie des informations de livraison et paiement. Un email de confirmation récapitulant la commande est envoyé à l'adresse indiquée. Relax se réserve le droit d'annuler toute commande présentant un caractère frauduleux.</p>
        </Section>
        <Section title="4. Paiement">
          <p>Le paiement s'effectue par carte bancaire (Carte Bleue, Visa, Mastercard) via la plateforme sécurisée Stripe, ou via PayPal. Le débit est réalisé au moment de la validation de la commande. Les données bancaires sont chiffrées et ne transitent jamais par nos serveurs.</p>
        </Section>
        <Section title="5. Livraison">
          <p>Les commandes sont expédiées sous 24h ouvrées vers la France, la Belgique, la Suisse et le Luxembourg. Les délais et tarifs sont détaillés dans la page Livraison & Retours. La livraison est offerte dès 25 € d'achat.</p>
        </Section>
        <Section title="6. Droit de rétractation">
          <p>Conformément aux articles L.221-18 et suivants du Code de la consommation, vous disposez de 14 jours légaux pour exercer votre droit de rétractation. Relax étend ce délai à 30 jours pour les produits non ouverts et non utilisés.</p>
        </Section>
        <Section title="7. Garanties et responsabilité">
          <p>Les produits bénéficient de la garantie légale de conformité (art. L.217-4 et suivants du Code de la consommation) et de la garantie contre les vices cachés (art. 1641 du Code civil). En cas de produit défectueux ou non conforme, contactez-nous pour un échange ou un remboursement.</p>
        </Section>
        <Section title="8. Litiges">
          <p>Les présentes CGV sont soumises au droit français. En cas de litige, une solution amiable sera recherchée en priorité. À défaut, vous pouvez recourir gratuitement au médiateur de la consommation ou saisir les tribunaux compétents.</p>
        </Section>
      </>
    ),
  },
  "mentions-legales": {
    title: "Mentions Légales",
    updated: "Dernière mise à jour : juillet 2026",
    body: (
      <>
        <Section title="Éditeur du site">
          <p>Relax Beauté — SAS au capital de 10 000 €<br />12 Rue de la Beauté, 75002 Paris, France<br />RCS Paris 000 000 000 (en cours d'immatriculation)<br />Email : contact@relax-beaute.fr</p>
        </Section>
        <Section title="Directrice de la publication">
          <p>La fondatrice de Relax Beauté, en sa qualité de représentante légale.</p>
        </Section>
        <Section title="Hébergement">
          <p>Le site est hébergé sur une infrastructure cloud sécurisée. Les données sont hébergées au sein de l'Union Européenne.</p>
        </Section>
        <Section title="Propriété intellectuelle">
          <p>L'ensemble des éléments du site (marque Relax, textes, visuels, logo, design) est protégé par le droit de la propriété intellectuelle. Toute reproduction, même partielle, est interdite sans autorisation écrite préalable.</p>
        </Section>
        <Section title="Paiement">
          <p>Les paiements par carte bancaire sont traités par Stripe Inc., et les paiements PayPal par PayPal (Europe) S.à r.l., prestataires de paiement agréés.</p>
        </Section>
      </>
    ),
  },
  confidentialite: {
    title: "Politique de Confidentialité",
    updated: "Dernière mise à jour : juillet 2026",
    body: (
      <>
        <Section title="1. Données collectées">
          <p>Dans le cadre de votre commande, nous collectons : identité (nom, prénom), coordonnées (email, téléphone), adresse de livraison et historique de commandes. Les données bancaires sont traitées exclusivement par nos prestataires de paiement (Stripe, PayPal) et ne sont jamais stockées par Relax.</p>
        </Section>
        <Section title="2. Finalités du traitement">
          <p>Vos données servent à : traiter et livrer vos commandes, assurer le suivi client (email/SMS), gérer les retours et remboursements, et — avec votre consentement — vous envoyer nos actualités beauté.</p>
        </Section>
        <Section title="3. Base légale et durée de conservation">
          <p>Les traitements reposent sur l'exécution du contrat (commande), nos obligations légales (facturation, 10 ans) et votre consentement (newsletter, révocable à tout moment). Les données de commande sont conservées 5 ans maximum.</p>
        </Section>
        <Section title="4. Vos droits (RGPD)">
          <p>Vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité et d'opposition. Pour exercer ces droits : contact@relax-beaute.fr. Vous pouvez également saisir la CNIL (cnil.fr) en cas de réclamation.</p>
        </Section>
        <Section title="5. Cookies">
          <p>Le site utilise des cookies strictement nécessaires au fonctionnement du panier et, avec votre accord, des cookies de mesure d'audience anonymisés. Vous pouvez les refuser à tout moment via les réglages de votre navigateur.</p>
        </Section>
        <Section title="6. Sécurité">
          <p>Nous mettons en œuvre des mesures techniques et organisationnelles appropriées : chiffrement SSL/TLS, accès restreints, paiements certifiés PCI-DSS via Stripe.</p>
        </Section>
      </>
    ),
  },
  "livraison-retours": {
    title: "Livraison & Retours",
    updated: "Dernière mise à jour : juillet 2026",
    body: (
      <>
        <Section title="Expédition">
          <p>Toutes les commandes passées avant 15h sont expédiées le jour même (jours ouvrés). Un email avec numéro de suivi vous est envoyé dès l'expédition.</p>
        </Section>
        <Section title="Modes et tarifs de livraison">
          <p>Colissimo Standard (2-3 jours ouvrés) : 3,90 €<br />Chronopost Express (24h) : 7,90 €<br />Livraison offerte dès 25 € d'achat (mode standard).</p>
          <p>Zones desservies : France métropolitaine, Belgique, Suisse, Luxembourg.</p>
        </Section>
        <Section title="Retours — Satisfait ou remboursé 30 jours">
          <p>Vous disposez de 30 jours après réception pour retourner tout produit non ouvert et non utilisé. Procédure :</p>
          <p>1. Écrivez-nous à contact@relax-beaute.fr avec votre numéro de commande.<br />2. Renvoyez le produit dans son emballage d'origine.<br />3. Remboursement sous 5 jours ouvrés après réception, sur le moyen de paiement utilisé.</p>
        </Section>
        <Section title="Produit défectueux ou erreur de commande">
          <p>En cas de produit endommagé ou d'erreur de notre part, les frais de retour sont intégralement pris en charge par Relax et un remplacement ou un remboursement complet vous est proposé.</p>
        </Section>
      </>
    ),
  },
};

const LegalPage = () => {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === params.get("tab")) ? params.get("tab") : "cgv";
  const content = CONTENT[tab];

  return (
    <div data-testid="legal-page" className="pt-28 sm:pt-36 pb-24 bg-cream min-h-screen">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <nav className="flex items-center gap-1.5 text-xs text-muted-warm mb-10">
          <Link to="/" className="hover:text-terracotta transition-colors">Accueil</Link>
          <ChevronRight size={12} />
          <span className="text-onyx font-medium">Informations légales</span>
        </nav>
        <FadeUp>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-onyx">Informations légales</h1>
          <p className="mt-3 text-sm text-muted-warm">Transparence totale, parce que la confiance fait partie du rituel.</p>
        </FadeUp>
        <div className="mt-10 flex flex-wrap gap-2" data-testid="legal-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`legal-tab-${t.id}`}
              onClick={() => setParams({ tab: t.id })}
              className={`rounded-full px-5 py-2.5 text-xs font-medium tracking-wide transition-all ${
                tab === t.id
                  ? "bg-onyx text-cream"
                  : "bg-white border border-blush-deep text-muted-warm hover:border-rose-gold hover:text-onyx"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <FadeUp className="mt-8">
          <div className="bg-white rounded-3xl border border-blush-deep p-7 sm:p-12" data-testid="legal-content">
            <h2 className="font-serif text-3xl text-onyx mb-2">{content.title}</h2>
            <p className="font-mono text-[11px] tracking-widest uppercase text-muted-warm mb-8">{content.updated}</p>
            {content.body}
            <p className="mt-10 pt-6 border-t border-blush-deep text-xs text-muted-warm">
              Une question ? Écrivez-nous à <span className="text-terracotta font-medium">contact@relax-beaute.fr</span> — nous répondons sous 24h ouvrées.
            </p>
          </div>
        </FadeUp>
      </div>
    </div>
  );
};

export default LegalPage;
